import { BookConstants } from '@/constants/bookConstants';
import { intersectLines } from './geometry';
import type {
  CloseBookMaskSide,
  CloseBookMaskStateInput,
  StaticLayerMaskRetainInput,
  Point,
  Rect,
  Segment
} from '@/types/book';

const { GEOM_EPSILON: EPSILON, MIN_MASK_AREA } = BookConstants;

/** 判断当前翻页是否处在封面或封底的闭合遮罩场景。 */
export function isCloseBookStaticMaskState(
  input: CloseBookMaskStateInput
): CloseBookMaskSide | null {
  if (input.displayMode !== 'double') return null;

  if (input.direction === 'back' && input.bottomPageIndex < 0) return 'front';
  if (input.direction === 'forward' && input.bottomPageIndex >= input.pageCount) return 'back';

  return null;
}

/** 判断上一帧静态层遮罩是否应保留，避免封面/封底临界帧闪烁。 */
export function shouldRetainStaticLayerMask(input: StaticLayerMaskRetainInput): boolean {
  return input.hasExistingMask && isCloseBookStaticMaskState(input) !== null;
}

/** 计算指定直线与矩形四条边的去重交点。 */
export function getLineRectIntersections(rect: Rect, line: Segment): Point[] {
  if (isDegenerateSegment(line)) return [];

  const right = rect.left + rect.width;
  const bottom = rect.top + rect.height;
  const edges: Segment[] = [
    [
      { x: rect.left, y: rect.top },
      { x: right, y: rect.top }
    ],
    [
      { x: right, y: rect.top },
      { x: right, y: bottom }
    ],
    [
      { x: right, y: bottom },
      { x: rect.left, y: bottom }
    ],
    [
      { x: rect.left, y: bottom },
      { x: rect.left, y: rect.top }
    ]
  ];
  const intersections: Point[] = [];

  for (const edge of edges) {
    let point: Point | null;
    try {
      point = intersectLines(line, edge);
    } catch {
      return [];
    }
    if (!point || !isPointInRect(rect, point)) continue;
    const normalized = normalizePoint(point);
    if (intersections.some((existing) => areSamePoint(existing, normalized))) continue;
    intersections.push(normalized);
  }

  return intersections;
}

/** 构造静态层需要排除的多边形区域。 */
export function buildStaticLayerExclusionPolygon(
  bookRect: Rect,
  tangentLine: Segment,
  side: CloseBookMaskSide
): Point[] | null {
  const intersections = getLineRectIntersections(bookRect, tangentLine);
  if (intersections.length < 2) return null;

  const sortedLine = sortByYThenX(intersections) as Segment;
  return toValidMaskPolygon(
    clipRectByLine(bookRect, sortedLine, side === 'front' ? 'left' : 'right')
  );
}

/** 构造静态层最终可见的多边形区域。 */
export function buildStaticLayerVisiblePolygon(
  bookRect: Rect,
  tangentLine: Segment,
  side: CloseBookMaskSide
): Point[] | null {
  const intersections = getLineRectIntersections(bookRect, tangentLine);
  if (intersections.length < 2) return null;

  const sortedLine = sortByYThenX(intersections) as Segment;
  return toValidMaskPolygon(
    clipRectByLine(bookRect, sortedLine, side === 'front' ? 'right' : 'left')
  );
}

/** 判断线段是否已经短到无法稳定表示折痕方向。 */
function isDegenerateSegment(line: Segment): boolean {
  const [start, end] = line;
  return Math.hypot(end.x - start.x, end.y - start.y) < EPSILON;
}

/** 判断点是否位于带容差的矩形范围内。 */
function isPointInRect(rect: Rect, point: Point): boolean {
  return (
    point.x >= rect.left - EPSILON &&
    point.x <= rect.left + rect.width + EPSILON &&
    point.y >= rect.top - EPSILON &&
    point.y <= rect.top + rect.height + EPSILON
  );
}

/** 判断两个点在容差范围内是否可视为同一点。 */
function areSamePoint(pointA: Point, pointB: Point): boolean {
  return Math.abs(pointA.x - pointB.x) < EPSILON && Math.abs(pointA.y - pointB.y) < EPSILON;
}

/** 清理接近 0 的坐标值，避免遮罩多边形出现微小噪声。 */
function normalizePoint(point: Point): Point {
  return {
    x: Math.abs(point.x) < EPSILON ? 0 : point.x,
    y: Math.abs(point.y) < EPSILON ? 0 : point.y
  };
}

/** 按 y 再按 x 排序交点，用于稳定构造切割线段。 */
function sortByYThenX(points: Point[]): Point[] {
  return [...points].sort((pointA, pointB) => {
    const yDelta = pointA.y - pointB.y;
    if (Math.abs(yDelta) >= EPSILON) return yDelta;
    return pointA.x - pointB.x;
  });
}

/** 使用直线裁剪矩形，保留指定方向一侧的多边形。 */
function clipRectByLine(rect: Rect, line: Segment, keepSide: 'left' | 'right'): Point[] {
  const right = rect.left + rect.width;
  const bottom = rect.top + rect.height;
  const rectPolygon = [
    { x: rect.left, y: rect.top },
    { x: right, y: rect.top },
    { x: right, y: bottom },
    { x: rect.left, y: bottom }
  ];
  const result: Point[] = [];

  for (let index = 0; index < rectPolygon.length; index += 1) {
    const current = rectPolygon[index];
    const next = rectPolygon[(index + 1) % rectPolygon.length];
    const currentInside = isPointOnLineSide(line, current, keepSide);
    const nextInside = isPointOnLineSide(line, next, keepSide);

    if (currentInside) result.push(current);

    if (currentInside !== nextInside) {
      const intersection = intersectLines(line, [current, next]);
      if (intersection) result.push(normalizePoint(intersection));
    }
  }

  return dedupeAdjacentPoints(result);
}

/** 判断点位于有向线段的左侧还是右侧。 */
function isPointOnLineSide(line: Segment, point: Point, side: 'left' | 'right'): boolean {
  const [start, end] = line;
  const cross = (end.x - start.x) * (point.y - start.y) - (end.y - start.y) * (point.x - start.x);

  return side === 'left' ? cross >= -EPSILON : cross <= EPSILON;
}

/** 移除相邻重复点，确保 Pixi 绘制的多边形稳定闭合。 */
function dedupeAdjacentPoints(points: Point[]): Point[] {
  const result: Point[] = [];

  for (const point of points) {
    if (result.length === 0 || !areSamePoint(result[result.length - 1], point)) {
      result.push(point);
    }
  }

  if (result.length > 1 && areSamePoint(result[0], result[result.length - 1])) {
    result.pop();
  }

  return result;
}

/** 过滤无法稳定绘制为 Pixi mask 的退化多边形。 */
function toValidMaskPolygon(points: Point[]): Point[] | null {
  if (points.length < 3) return null;
  if (getPolygonArea(points) < MIN_MASK_AREA) return null;
  return points;
}

/** 使用鞋带公式计算多边形面积。 */
function getPolygonArea(points: Point[]): number {
  let area = 0;

  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    area += current.x * next.y - next.x * current.y;
  }

  return Math.abs(area) / 2;
}
